import { useState } from 'react'
import { PageHeader } from '../../components/PageHeader'
import type { PassPlayPlayer } from './types'
import { useT } from '../../i18n'

interface AiPersona {
  id: string
  name: string
  avatarEmoji?: string | null
}

interface Props {
  /** Game label, e.g. "I'd Eat That · Pass Play". */
  title: string
  /** Tailwind game-color classes for primary action button. */
  primaryButtonClass: string
  /** AI persona pool for this game. Player can pick zero or more. */
  aiPool: AiPersona[]
  /** Min/max total seats (humans + AI). */
  minPlayers: number
  maxPlayers: number
  /** What happens when the user taps "Start". */
  onStart: (players: PassPlayPlayer[]) => void
  onBack: () => void
}

/**
 * Phone Pass Play setup. Local only — one device, multiple humans
 * (each typing a name) + AI personas. Used by all three games.
 *
 * Players are stored in the order they're added so reveal/voting
 * cycles are deterministic. Humans get an empty name input the
 * caller must fill in before the seat counts; AI seats default to
 * the persona name (editable). At least one human is required so
 * that the device actually has someone to pass to.
 */
export function PassPlaySetupScreen({
  title, primaryButtonClass, aiPool, minPlayers, maxPlayers, onStart, onBack,
}: Props) {
  const t = useT()
  const [humans, setHumans] = useState<string[]>([''])
  const [pickedAis, setPickedAis] = useState<Set<string>>(new Set())

  const totalSeats = humans.filter(n => n.trim().length > 0).length + pickedAis.size
  const namedHumans = humans.filter(n => n.trim().length > 0).length

  const canStart =
    namedHumans >= 1 &&
    totalSeats >= minPlayers &&
    totalSeats <= maxPlayers

  function addHuman() {
    if (totalSeats >= maxPlayers) return
    setHumans(prev => [...prev, ''])
  }

  function removeHuman(idx: number) {
    setHumans(prev => prev.filter((_, i) => i !== idx))
  }

  function setHumanName(idx: number, name: string) {
    setHumans(prev => prev.map((n, i) => i === idx ? name.slice(0, 24) : n))
  }

  function toggleAi(id: string) {
    setPickedAis(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else if (totalSeats < maxPlayers) next.add(id)
      return next
    })
  }

  function start() {
    if (!canStart) return
    const players: PassPlayPlayer[] = []
    let n = 0
    for (const name of humans) {
      const trimmed = name.trim()
      if (!trimmed) continue
      players.push({ id: `h${n++}`, kind: 'human', name: trimmed })
    }
    for (const ai of aiPool) {
      if (!pickedAis.has(ai.id)) continue
      players.push({
        id: `a${n++}`,
        kind: 'ai',
        name: ai.name,
        aiPlayerId: ai.id,
        avatarEmoji: ai.avatarEmoji ?? '🤖',
      })
    }
    onStart(players)
  }

  return (
    <div className="screen">
      <PageHeader title={title} subtitle={t('Phone Pass Play · local only')} onBack={onBack} />
      <div className="flex-1 flex flex-col px-5 py-4 gap-5 overflow-y-auto pb-32 relative z-10">
        <div className="card p-3 bg-brand-elevated">
          <p className="text-xs text-brand-muted leading-relaxed">{t('Add the people sitting around you and any AI players. The phone passes between humans during writing and voting, and the app shows a splash before each turn so nobody peeks.')}</p>
        </div>

        <section>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-muted">
              {t('People here ({n})', { n: namedHumans })}
            </p>
            <button
              type="button"
              onClick={addHuman}
              disabled={totalSeats >= maxPlayers}
              className="text-xs font-semibold underline text-brand-text disabled:opacity-30"
            >
              {t('+ Add player')}
            </button>
          </div>
          <ul className="space-y-2">
            {humans.map((name, idx) => (
              <li key={idx} className="card p-3 flex items-center gap-3">
                <input
                  type="text"
                  placeholder={t('Player {n} name', { n: idx + 1 })}
                  value={name}
                  onChange={e => setHumanName(idx, e.target.value)}
                  className="flex-1 bg-transparent text-brand-text font-semibold
                             placeholder:text-brand-muted/60 focus:outline-hidden"
                  maxLength={24}
                />
                {humans.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeHuman(idx)}
                    className="text-xs text-brand-muted active:text-brand-danger"
                  >
                    Remove
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>

        <section>
          <p className="text-xs font-semibold uppercase tracking-wide text-brand-muted mb-2">
            {t('AI players ({n})', { n: pickedAis.size })}
          </p>
          <ul className="space-y-2">
            {aiPool.map(ai => {
              const picked = pickedAis.has(ai.id)
              const atCap = totalSeats >= maxPlayers && !picked
              return (
                <li key={ai.id}>
                  <button
                    type="button"
                    onClick={() => toggleAi(ai.id)}
                    disabled={atCap}
                    className={
                      'w-full card p-3 flex items-center gap-3 text-left transition-all ' +
                      (picked
                        ? 'border-2 border-brand-text bg-brand-elevated'
                        : 'border border-brand-border') +
                      (atCap ? ' opacity-40' : '')
                    }
                  >
                    <span className="text-2xl shrink-0">{ai.avatarEmoji ?? '🤖'}</span>
                    <span className="flex-1 font-semibold text-brand-text">{ai.name}</span>
                    <span className={
                      'text-xs font-semibold ' +
                      (picked ? 'text-brand-text' : 'text-brand-muted')
                    }>
                      {picked ? t('In') : t('Add')}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        </section>
      </div>
      <div className="floating-actions">
        <button
          type="button"
          onClick={start}
          disabled={!canStart}
          className={`${primaryButtonClass} disabled:opacity-40`}
        >
          {totalSeats < minPlayers
            ? (minPlayers - totalSeats === 1
                ? t('Need 1 more player')
                : t('Need {n} more players', { n: minPlayers - totalSeats }))
            : namedHumans < 1
              ? t('Type at least one player name')
              : (totalSeats === 1
                  ? t('Start with 1 player')
                  : t('Start with {n} players', { n: totalSeats }))}
        </button>
      </div>
    </div>
  )
}
